import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { CATEGORIES, Category, BudgetEntry } from '../db/database';
import { useBudget } from '../context/BudgetContext';
import { format } from 'date-fns';

interface EditEntryFormProps {
  open: boolean;
  onClose: () => void;
  entry: BudgetEntry | null;
}

const EditEntryForm: React.FC<EditEntryFormProps> = ({ open, onClose, entry }) => {
  const { updateEntry } = useBudget();
  const [type, setType] = useState<'income' | 'expense'>('expense');
  const [category, setCategory] = useState<Category>('Other');
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [errors, setErrors] = useState<{ title?: string; value?: string }>({});

  useEffect(() => {
    if (entry) {
      setType(entry.type);
      setCategory(entry.category);
      setTitle(entry.title);
      setValue(entry.value.toString());
      setDate(format(new Date(entry.date), 'yyyy-MM-dd'));
      setIsRecurring(entry.isRecurring || false);
      setRecurringFrequency(entry.recurringFrequency || 'monthly');
    }
  }, [entry]);

  const handleTypeChange = (_: React.MouseEvent<HTMLElement>, newType: 'income' | 'expense' | null) => {
    if (newType !== null) {
      setType(newType);
      if (newType === 'income' && category !== 'Income') {
        setCategory('Income');
      }
    }
  };

  const validate = () => {
    const newErrors: { title?: string; value?: string } = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    const numValue = parseFloat(value);
    if (!value || isNaN(numValue) || numValue <= 0) {
      newErrors.value = 'Please enter a valid positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate() || !entry?.id) return;

    try {
      await updateEntry(entry.id, {
        type,
        category,
        title: title.trim(),
        value: parseFloat(value),
        date: new Date(date),
        isRecurring,
        recurringFrequency: isRecurring ? recurringFrequency : undefined,
      });

      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{
            bgcolor: 'warning.main',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <EditIcon sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Edit Entry
          </Typography>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Type Toggle */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Type
              </Typography>
              <ToggleButtonGroup
                value={type}
                exclusive
                onChange={handleTypeChange}
                fullWidth
                color={type === 'income' ? 'success' : 'error'}
              >
                <ToggleButton value="income" sx={{ py: 1.5 }}>
                  Income
                </ToggleButton>
                <ToggleButton value="expense" sx={{ py: 1.5 }}>
                  Expense
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Category */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value as Category)}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Title */}
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
              required
              placeholder="e.g., Grocery shopping, Salary, etc."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            {/* Value */}
            <TextField
              label="Amount"
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              error={!!errors.value}
              helperText={errors.value}
              fullWidth
              required
              inputProps={{
                step: '0.01',
                min: '0',
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">£</InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            {/* Date */}
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            {/* Recurring */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                  />
                }
                label="Recurring transaction"
              />
              
              {isRecurring && (
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={recurringFrequency}
                    label="Frequency"
                    onChange={(e) => setRecurringFrequency(e.target.value as 'weekly' | 'monthly' | 'yearly')}
                  >
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditEntryForm;
